import { useEffect, useMemo, useRef } from 'react'
import {
  FiCheck,
  FiEdit2,
  FiMessageCircle,
  FiPlus,
  FiThumbsUp,
  FiTrash2,
} from 'react-icons/fi'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import WebinarSuggestionModal from '@presentation/components/sections/webinars/WebinarSuggestionModal'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useWebinarSuggestions } from '@presentation/hooks/useWebinarSuggestions'
import { useWebinarSuggestionsActions } from '@presentation/hooks/useWebinarSuggestionsActions'
import { useWebinarSuggestionCommentsCounts } from '@presentation/hooks/useWebinarSuggestionCommentsCounts'
import SeoHead from '@presentation/seo/SeoHead'

function formatSubmittedOn(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function WebinarSuggestionsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuthUser()
  const {
    suggestions,
    loading: suggestionsLoading,
    error: suggestionsError,
    reload: reloadSuggestions,
  } = useWebinarSuggestions()

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const {
    isSuggestionModalOpen,
    editingSuggestion,
    modalSubmitting,
    modalError,
    actionError,
    activeLikeId,
    activeDeleteId,
    openCreateSuggestionModal,
    openEditSuggestionModal,
    closeSuggestionModal,
    handleSubmitSuggestion,
    handleToggleSuggestionLike,
    handleDeleteSuggestion,
  } = useWebinarSuggestionsActions({
    user,
    onAuthRequired: openLoginModal,
    onSuggestionsChanged: reloadSuggestions,
  })
  const hasHandledInitialSuggestionsLoad = useRef(false)

  useEffect(() => {
    if (!hasHandledInitialSuggestionsLoad.current) {
      hasHandledInitialSuggestionsLoad.current = true
      return
    }
    reloadSuggestions()
  }, [user?.uid, reloadSuggestions])

  const rankedSuggestions = useMemo(() => {
    const byUid = new Map<string, (typeof suggestions)[number]>()
    for (const suggestion of suggestions) {
      if (!byUid.has(suggestion.uid)) {
        byUid.set(suggestion.uid, suggestion)
      }
    }

    return [...byUid.values()].sort((left, right) => {
      if (right.likesCount !== left.likesCount) {
        return right.likesCount - left.likesCount
      }
      return right.submittedOn.getTime() - left.submittedOn.getTime()
    })
  }, [suggestions])
  const rankedSuggestionIds = useMemo(
    () => rankedSuggestions.map((suggestion) => suggestion.uid),
    [rankedSuggestions],
  )
  const commentCounts = useWebinarSuggestionCommentsCounts(rankedSuggestionIds)

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      <SeoHead
        title="Suggested Webinars | Parkinson's Insights"
        description="Community-ranked webinar topic suggestions from Parkinson's Insights members."
        canonicalPath="/webinars/suggestions"
        noindex
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>

        <div className="mt-5 ml-1 flex items-start justify-between gap-3 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
              Suggested Webinars
            </h1>
            <p className="mt-1 text-sm text-text-secondary sm:text-base">
              Community-ranked webinar subjects from highest to lowest interest.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateSuggestionModal}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-blue px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue/90"
          >
            <FiPlus className="size-4 text-white" aria-hidden="true" />
            Submit New
          </button>
        </div>

        {(suggestionsError || actionError) ? (
          <p className="mt-4 text-sm font-semibold text-text-secondary">
            {suggestionsError ?? actionError}
          </p>
        ) : null}

        {suggestionsLoading ? (
          <p className="mt-6 text-sm font-semibold text-text-secondary">
            Loading webinar suggestions...
          </p>
        ) : rankedSuggestions.length === 0 ? (
          <p className="mt-6 text-sm font-semibold text-text-secondary">
            No webinar suggestions yet.
          </p>
        ) : (
          <div className="mt-6 space-y-3">
            {rankedSuggestions.map((suggestion) => {
              const liked = suggestion.likedByCurrentUser === true
              const isLikePending = activeLikeId === suggestion.uid
              const isDeletePending = activeDeleteId === suggestion.uid
              const canManageSuggestion = user?.isAdmin === true || user?.uid === suggestion.submittedByUid

              return (
                <article
                  key={suggestion.uid}
                  className="rounded-xl bg-card-surface p-4 shadow-lg shadow-black/20 ring-1 ring-black/5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {suggestion.adminReplyLabel ? (
                        <span
                          className="mb-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-bold"
                          style={{
                            backgroundColor: 'var(--color-green-light)',
                            color: 'var(--color-green)',
                            borderColor: 'var(--color-green)',
                          }}
                        >
                          <FiCheck className="h-4 w-4" aria-hidden="true" />
                          {suggestion.adminReplyLabel}
                        </span>
                      ) : null}
                      <h4 className="text-xl font-bold text-text-primary">{suggestion.subject}</h4>
                      <p className="mt-1 text-sm text-text-primary">{suggestion.description}</p>
                      <p className="mt-2 text-xs font-semibold text-text-muted">
                        {suggestion.submittedByName || suggestion.submittedByUid || 'Unknown user'}
                        {' · '}
                        {formatSubmittedOn(suggestion.submittedOn)}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          AnalyticsFeature.trackContentOpen(
                            `${location.pathname}${location.search || ''}`,
                            'webinar_suggestion_comment_thread',
                            suggestion.uid,
                            'webinar_suggestions',
                            Boolean(user),
                          )
                          navigate(`/webinars/suggestions/${suggestion.uid}/comments`)
                        }}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue/90"
                      >
                        <FiMessageCircle className="h-4 w-4" />
                        Comments ({commentCounts[suggestion.uid] ?? 0})
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        void handleToggleSuggestionLike(
                          suggestion.uid,
                          suggestion.submittedByUid,
                          suggestion.subject,
                        )
                      }}
                      disabled={isLikePending}
                      className={`inline-flex shrink-0 items-center gap-2 rounded-full border border-blue px-3 py-2 text-sm font-semibold transition-colors ${
                        liked
                          ? 'bg-blue text-white'
                          : 'bg-surface text-blue hover:bg-blue/10'
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                      aria-label="Toggle webinar suggestion like"
                    >
                      <FiThumbsUp className="h-4 w-4" />
                      {suggestion.likesCount}
                    </button>
                  </div>

                  {canManageSuggestion ? (
                    <div className="mt-3 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditSuggestionModal(suggestion)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
                        aria-label="Edit webinar suggestion"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void handleDeleteSuggestion(suggestion)
                        }}
                        disabled={isDeletePending}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label="Delete webinar suggestion"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </article>
              )
            })}
          </div>
        )}
      </div>

      <WebinarSuggestionModal
        key={`${editingSuggestion?.uid ?? 'create'}-${isSuggestionModalOpen ? 'open' : 'closed'}`}
        isOpen={isSuggestionModalOpen}
        mode={editingSuggestion ? 'edit' : 'create'}
        initialValue={
          editingSuggestion
            ? {
                subject: editingSuggestion.subject,
                description: editingSuggestion.description,
                adminReplyLabel: editingSuggestion.adminReplyLabel,
              }
            : undefined
        }
        showAdminReplyLabel={user?.isAdmin === true}
        isSubmitting={modalSubmitting}
        error={modalError}
        onSubmit={(input) => {
          void handleSubmitSuggestion(input)
        }}
        onClose={closeSuggestionModal}
      />
    </div>
  )
}
